/* SliderComponent */
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging -- disabled to allow to inherit from AbstractComponent */
import { Component, Element, Event, EventEmitter, h, Listen, Method, Prop, State } from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars
import { AbstractComponent } from '@cewe-designsystem/abstract';
import { ChangeEvent, LinkClickEvent, PreviewRegister, PreviewSelect } from './types/events';
import { PossibleResponsiveSettingsOptions, ResponsiveSettings, SetActivePreviewFunction } from './types/interfaces';
import SliderTemplate from './templates/cwc-slider.template';
import UtilBrowserDetection from '@cewe-designsystem/util_browser-detection';

@Component({
  tag: 'cwc-slider',
  styleUrl: './styles/cwc-slider.scss',
  shadow: true,
})
export class SliderComponent {
  /**
   * element
   */
  @Element() element: HTMLElement;

  @Prop({ mutable: true }) itemsPerPage: number = 1;
  /**
   * @deprecated
   */
  @Prop() loop: boolean = false;
  @Prop() autoslide: boolean = false;
  @Prop() autoslideDelay: number = 8000;
  @Prop() bullets: boolean = true;
  @Prop() osDetection: boolean = false;
  @Prop({ mutable: true }) gap: number = 0;
  @Prop({ mutable: true }) arrows: boolean = true;
  @Prop({ mutable: true }) responsiveSettings: ResponsiveSettings | string | undefined;

  @State() snap: boolean = true;
  @State() dragging: boolean = false;
  @State() mouseMoving: boolean = false;
  @State() sliderIsMoving: boolean = false;
  @State() visibleSlides: Array<number> = [0];
  @State() stageWidth: number = 0;

  private autoslideInterval: ReturnType<typeof setInterval> | undefined;
  private autoslideIntersectionObserver: IntersectionObserver;
  private resizeObserver: ResizeObserver;

  // this function gets registered if the preview component is connected
  private setActivePreview: SetActivePreviewFunction;

  canvas: HTMLElement;
  stage: HTMLElement;
  slides: NodeListOf<HTMLElement>;

  /**
   * Event thrown when visible slides change
   *
   * @docs
   * @event on:slider:changed
   */
  @Event({
    eventName: 'on:slider:changed',
  })
  onSliderChanged: EventEmitter<ChangeEvent>;

  /**
   * Event thrown when a link inside a slide was clicked
   *
   * @docs
   * @event on:slider:link-clicked
   */
  @Event({
    eventName: 'on:slider:link-clicked',
  })
  onSliderLinkClicked: EventEmitter<LinkClickEvent>;

  @Listen('on:slider-preview:register', { passive: true })
  handlePreviewRegister(event: CustomEvent<PreviewRegister>) {
    this.setActivePreview = event.detail.setActivePreview;
    this.setActivePreview(this.visibleSlides);
  }

  @Listen('on:slider-preview:select', { passive: true })
  handlePreviewSelect(event: CustomEvent<PreviewSelect>) {
    this.slideTo(event.detail.selectedPreviewIndex);
  }

  handleSlotChange() {
    const newSlots: NodeListOf<HTMLElement> = this.element.querySelectorAll(':scope > *:not([slot])');

    if (
      this.slides.length !== newSlots.length ||
      Array.from(newSlots).some((element, index) => this.slides.item(index) !== element)
    ) {
      // setting visible slides by number of pages and handles os detection
      this.setInitialVisibleSlides();

      if (this.loop) {
        this.element.querySelectorAll(':scope > [data-origin]').forEach((element) => element.remove());
        this.createClones();
      } else {
        this.slides = newSlots;
      }

      this.slideTo(this.visibleSlides[0], 'instant');
    }
  }

  /**
   * Called every time the component is connected to the DOM.
   * When the component is first connected, this method is called before componentWillLoad.
   * It is important to note that this method can be called more than once, everytime, the element is attached or moved in the DOM.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec
   */
  connectedCallback() {
    this.slides = this.element.querySelectorAll(':scope > *:not([slot])');

    // setting visible slides by number of pages and handles os detection
    this.setInitialVisibleSlides();

    if (this.loop) {
      this.createClones();
    }

    // parse responsive settings when available and add default values
    if (this.responsiveSettings) {
      // parse json from html to object
      if (typeof this.responsiveSettings === 'string') {
        this.responsiveSettings = JSON.parse(this.responsiveSettings);
      }

      // add default values from props for backwards compatibility
      this.responsiveSettings = Object.assign(
        {
          0: {
            itemsPerPage: this.itemsPerPage,
            arrows: this.arrows,
            gap: this.gap,
          },
        },
        this.responsiveSettings,
      );
    }

    this.resizeObserver = new ResizeObserver(async () => {
      this.stageWidth = this.stage.getBoundingClientRect().width;

      if (this.responsiveSettings) {
        // apply resize event with responsive config handling
        this.hanldeResizeResponsiveSettings();
      }
    });

    this.resizeObserver.observe(this.element);

    if (this.autoslide) {
      this.startAutoslideIntersectionOberver();
    }
  }

  /**
   * Called every time the component is disconnected from the DOM, i.e., it calls be dispatched more than once
   * DO not confuse with a "onDestroy" kind of event.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec.
   */
  disconnectedCallback() {
    this.autoslideIntersectionObserver?.disconnect();
    this.resizeObserver?.disconnect();
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * A promise can be returned, that can be used to wait for the first render.
   */
  componentWillLoad() {
    // fire event when a content link (cta) is clicked
    this.element.querySelectorAll(':not([slot]) a[href]').forEach((link) => {
      link.addEventListener('click', () => this.onSliderLinkClicked.emit());
    });
  }

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   */
  componentDidLoad() {
    // scroll to actual element when clones exist
    if (this.loop || this.visibleSlides[0] != 0) {
      this.slideTo(this.visibleSlides[0], 'instant');
    }

    this.handleTouchDragging();
    this.handleMouseDragging();
    this.addKeyDownEventListener();

    // delay on scroll handling for initial scrolling when loop is active
    setTimeout(() => this.handleOnScroll(), 50); /* eslint-disable-line no-magic-numbers */
  }

  /**
   * Called before every render().
   * A promise can be returned, that can be used to wait for the upcoming render.
   */
  componentWillRender() {}

  /**
   * Called after every render().
   */
  componentDidRender() {}

  /**
   * Called when the component is about to be updated because some Prop() or State() changed.
   * It is never called during the first render().
   * A promise can be returned, that can be used to wait for the next render.
   */
  componentWillUpdate() {}

  /**
   * Called just after the component updates. It's never called during the first render().
   */
  componentDidUpdate() {}

  canSlideToNext(): boolean {
    return this.loop || Math.max(...this.visibleSlides) < this.slides.length - 1;
  }

  canSlideToPrevious(): boolean {
    return this.loop || Math.min(...this.visibleSlides) > 0;
  }

  slideToNext(): void {
    if (this.sliderIsMoving || !this.canSlideToNext()) {
      return;
    }

    this.slideTo(this.visibleSlides[0] + 1);
  }

  slideToPrevious(): void {
    if (this.sliderIsMoving || !this.canSlideToPrevious()) {
      return;
    }

    this.slideTo(this.visibleSlides[0] - 1);
  }

  slideToNearest(): void {
    this.stage.scrollTo({
      left: this.getIndexFromScrollLeft() * (this.getSlidesWidth() + this.gap),
      behavior: 'smooth',
    });
  }

  @Method()
  async slideTo(slide: number, behavior: string = 'smooth'): Promise<void> {
    const slidesWidth = this.getSlidesWidth();

    this.stage.scrollTo({
      left: slide * (slidesWidth + this.gap) + this.getFirstSlideIndex() * (slidesWidth + this.gap),
      // @ts-expect-error migrated from ts-ignore behaviour does not have the option instant that should be available
      behavior,
    });
  }

  private setInitialVisibleSlides() {
    let initialIndex = 0;

    if (this.osDetection) {
      const os = new UtilBrowserDetection().getOs();
      const foundIndex = Array.from(this.slides).findIndex((slide) => slide.getAttribute('data-os') == os);
      initialIndex = foundIndex == -1 ? 0 : foundIndex;
    }

    this.visibleSlides = Array.from({ length: this.itemsPerPage }, (_, i) => initialIndex + i);
  }

  private hanldeResizeResponsiveSettings() {
    const width = this.element.offsetWidth;

    const responseConfig: PossibleResponsiveSettingsOptions = {};
    Object.keys(this.responsiveSettings)
      .map((key) => parseInt(key))
      .sort(function (a, b) {
        return a - b;
      })
      .every((settingWidth) => {
        if (width > settingWidth) {
          Object.assign(responseConfig, this.responsiveSettings[settingWidth]);
          return true;
        }
        return false;
      });

    if (responseConfig.itemsPerPage && this.itemsPerPage !== responseConfig.itemsPerPage) {
      this.itemsPerPage = responseConfig.itemsPerPage;
      this.updateVisibleSlides();
    }
    this.arrows = responseConfig.arrows ?? this.arrows;
    this.gap = responseConfig.gap ?? this.gap;
  }

  private startAutoslideIntersectionOberver() {
    this.autoslideIntersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.startAutoslideWhenActive();

          return;
        }

        clearInterval(this.autoslideInterval);
      });
    });

    this.autoslideIntersectionObserver.observe(this.element);
  }

  getSlidesWithoutClones(): Array<Node> {
    return Array.from(this.slides).filter((slide) => !slide.hasAttribute('data-origin'));
  }

  private handleTouchDragging(): void {
    this.stage.addEventListener(
      'touchstart',
      () => {
        this.dragging = true;

        document.addEventListener(
          'touchend',
          () => {
            this.dragging = false;

            setTimeout(() => this.updateVisibleSlides(), 500); /* eslint-disable-line no-magic-numbers */
          },
          { once: true, passive: true },
        );
      },
      { passive: true },
    );
  }

  private handleMouseDragging(): void {
    let startX: number = 0;
    let scrollLeft: number = 0;
    let snapResetTimeout: ReturnType<typeof setInterval> | undefined = null;

    const handleMouseMove = (event: MouseEvent) => {
      this.snap = false;
      this.mouseMoving = true;

      if (!this.dragging || event.buttons === 0) {
        return;
      }

      this.stage.scrollLeft = scrollLeft - (event.clientX - startX);
    };

    this.stage.addEventListener('mousedown', (event: MouseEvent) => {
      event.preventDefault();
      this.stage.focus();

      //clear stackable timeout
      clearTimeout(snapResetTimeout);

      startX = event.clientX;
      scrollLeft = this.stage.scrollLeft;

      this.dragging = true;

      document.addEventListener(
        'mouseup',
        (event: MouseEvent) => {
          if (!this.dragging) {
            return;
          }

          this.dragging = false;
          this.mouseMoving = false;

          snapResetTimeout = setTimeout(
            () => (this.snap = !this.dragging),
            1000 /* eslint-disable-line no-magic-numbers */,
          );

          document.removeEventListener('mousemove', handleMouseMove);

          const distance = event.clientX - startX;

          // manually trigger scroll end because of dragging it wouldnt trigger
          if (distance == 0) {
            this.handleOnScrollEnd();
          }

          // should slide to next?
          /* eslint-disable-next-line no-magic-numbers */
          if (distance <= -50 && distance > -(this.getSlidesWidth() + this.gap) / 2) {
            this.slideToNext();

            return;
          }

          // should slide to prev?
          /* eslint-disable-next-line no-magic-numbers */
          if (distance >= 50 && distance < (this.getSlidesWidth() + this.gap) / 2) {
            this.slideToPrevious();

            return;
          }

          this.slideToNearest();
        },
        { once: true, passive: true },
      );

      document.addEventListener('mousemove', handleMouseMove, { passive: true });
    });
  }

  addKeyDownEventListener() {
    this.canvas.addEventListener('keydown', (event: KeyboardEvent) => {
      let keyFound = true;

      switch (event.key) {
        case 'ArrowRight':
          this.slideToNext();
          break;
        case 'ArrowLeft':
          this.slideToPrevious();
          break;
        case 'Home':
          this.slideTo(0);
          break;
        case 'End':
          this.slideTo(this.getSlidesWithoutClones().length - this.itemsPerPage);
          break;
        default:
          keyFound = false;
          break;
      }

      if (keyFound) {
        event.stopPropagation();
        event.preventDefault();
      }
    });
  }

  private handleOnScroll(): void {
    let scrollEndTimeout: ReturnType<typeof setInterval> | undefined = null;

    this.stage.addEventListener(
      'scroll',
      () => {
        clearTimeout(scrollEndTimeout);

        this.sliderIsMoving = true;

        scrollEndTimeout = setTimeout(() => this.handleOnScrollEnd(), 50); /* eslint-disable-line no-magic-numbers */
      },
      { passive: true },
    );
  }

  private handleOnScrollEnd(): void {
    this.sliderIsMoving = false;

    if (this.dragging) {
      return;
    }

    this.updateVisibleSlides();

    if (this.loop && this.currentSlideIsClone()) {
      this.slideTo(this.visibleSlides[0], 'instant');
    }

    this.startAutoslideWhenActive();
  }

  private startAutoslideWhenActive(): void {
    if (!this.autoslide) {
      return;
    }

    clearInterval(this.autoslideInterval);

    this.autoslideInterval = setInterval(() => {
      if (this.dragging) {
        return;
      }

      this.slideToNext();
    }, this.autoslideDelay);
  }

  private createClones(): void {
    // add clones after actual slide items (append)
    this.slides.forEach((slide, index) => {
      const clone = slide.cloneNode(true) as HTMLElement;

      clone.setAttribute('data-origin', index.toString());

      this.element.appendChild(clone);
    });

    // add clones before actual slide items (prepend)
    Array.from(this.slides)
      .reverse()
      .forEach((slide, index) => {
        const clone = slide.cloneNode(true) as HTMLElement;

        clone.setAttribute('data-origin', (this.slides.length - index - 1).toString());

        this.element.prepend(clone);
      });

    this.slides = this.element.querySelectorAll(':scope > *:not([slot])');
  }

  private currentSlideIsClone(): boolean {
    const index = this.getIndexFromScrollLeft();

    return this.slides.item(index).hasAttribute('data-origin');
  }

  private getSlidesWidth(): number {
    return this.slides.item(0).clientWidth;
  }

  private getFirstSlideIndex(): number {
    return Array.from(this.slides).findIndex((slide) => !slide.hasAttribute('data-origin'));
  }

  private getIndexFromScrollLeft(): number {
    return Math.round(this.stage.scrollLeft / (this.getSlidesWidth() + this.gap));
  }

  private getFirstVisibleSlideIndex(): number {
    const index = this.getIndexFromScrollLeft();

    const slideItem = this.slides.item(index);

    if (slideItem.hasAttribute('data-origin')) {
      return Number(slideItem.getAttribute('data-origin'));
    }

    return index % this.getSlidesWithoutClones().length;
  }

  private updateVisibleSlides(): void {
    const newVisibleSlides = Array.from(
      { length: this.itemsPerPage },
      (_, i) => (this.getFirstVisibleSlideIndex() + i) % this.getSlidesWithoutClones().length,
    );

    if (this.visibleSlides === newVisibleSlides) {
      return;
    }

    this.visibleSlides = newVisibleSlides;

    this.onSliderChanged.emit({ visibleSlides: this.visibleSlides });

    this.setActivePreview?.(this.visibleSlides);
  }

  /**
   * render
   */
  render() {
    return <SliderTemplate controller={this} />;
  }
}

/**
 * mixin abstract functions
 */
Object.setPrototypeOf(SliderComponent.prototype, AbstractComponent.prototype);

export interface SliderComponent extends AbstractComponent {}
